import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Progress, Button, Typography, message, Space } from 'antd';
import { CheckCircleFilled, LoadingOutlined, MinusCircleOutlined, CloseCircleFilled, ReloadOutlined } from '@ant-design/icons';
import {
  lanzarMotor,
  getMotorEnCurso,
  getProgreso,
  type ReconciliacionProgreso,
} from '../../api/reconciliacionApi';

const { Text, Title } = Typography;

interface MotorProgressOverlayProps {
  visible: boolean;
  modo: string;
  opcion?: string;
  onClose: () => void;
  onCompleted: () => void;
}

type OverlayState = 'launching' | 'polling' | 'completed' | 'error' | 'launch_failed';

// Mapeo de fases del motor a líneas UI
// Las fases 0-2 se agrupan en "Lectura de datos"
// Fase 3 → Farmacias, 4 → Farmacéuticos, 5 → Relaciones, 6 → Traslados, 7-8 → Escritura
interface FaseUI {
  key: string;
  label: string;
  completedWhenFase: number; // se marca ✓ cuando fase >= este valor
  getDetail?: (p: ReconciliacionProgreso) => string | null;
  hasProgress?: boolean; // barra de progreso (solo fase 5)
}

const FASES_UI: FaseUI[] = [
  {
    key: 'lectura',
    label: 'Lectura de datos',
    completedWhenFase: 3,
  },
  {
    key: 'farmacias',
    label: 'Farmacias',
    completedWhenFase: 4,
    getDetail: (p) => {
      if (p.fase >= 4 || (p.fase === 3 && p.totalFarmaciasAltas + p.totalFarmaciasBajas > 0)) {
        const parts = [];
        if (p.totalFarmaciasAltas > 0) parts.push(`${p.totalFarmaciasAltas} altas`);
        if (p.totalFarmaciasBajas > 0) parts.push(`${p.totalFarmaciasBajas} bajas`);
        return parts.length > 0 ? parts.join(', ') : 'sin cambios';
      }
      return null;
    },
  },
  {
    key: 'farmaceuticos',
    label: 'Farmacéuticos',
    completedWhenFase: 5,
    getDetail: (p) => {
      if (p.fase >= 5 || (p.fase === 4 && p.totalFarmaceuticos > 0)) {
        return p.totalFarmaceuticos > 0 ? `${p.totalFarmaceuticos} sin vincular` : 'sin cambios';
      }
      return null;
    },
  },
  {
    key: 'relaciones',
    label: 'Relaciones',
    completedWhenFase: 6,
    hasProgress: true,
    getDetail: (p) => {
      if (p.fase === 5 && p.farmaciasAnalizadas > 0) {
        return `${p.farmaciasProcesadas} / ${p.farmaciasAnalizadas} farmacias`;
      }
      if (p.fase >= 6) {
        const parts = [];
        if (p.totalTransmisiones > 0) parts.push(`${p.totalTransmisiones} transmisiones`);
        if (p.totalEmpleadosAE > 0) parts.push(`${p.totalEmpleadosAE} altas empl.`);
        if (p.totalEmpleadosCE > 0) parts.push(`${p.totalEmpleadosCE} cierres empl.`);
        return parts.length > 0 ? parts.join(', ') : 'sin cambios';
      }
      return null;
    },
  },
  {
    key: 'traslados',
    label: 'Traslados',
    completedWhenFase: 7,
    getDetail: (p) => {
      if (p.fase >= 7) {
        return p.totalTraslados > 0 ? `${p.totalTraslados} detectados` : 'sin cambios';
      }
      return null;
    },
  },
  {
    key: 'escritura',
    label: 'Escritura en BBDD',
    completedWhenFase: 9,
  },
];

const FASE_NAMES: Record<number, string> = {
  0: 'Inicio',
  1: 'Lectura CU2',
  2: 'Lectura Gescom',
  3: 'Farmacias',
  4: 'Farmacéuticos',
  5: 'Relaciones',
  6: 'Traslados',
  7: 'Informe',
  8: 'Escritura',
  9: 'Completado',
};

const MotorProgressOverlay: React.FC<MotorProgressOverlayProps> = ({ visible, modo, opcion = 'titulares', onClose, onCompleted }) => {
  const [state, setState] = useState<OverlayState>('launching');
  const [progreso, setProgreso] = useState<ReconciliacionProgreso | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((recId: number) => {
    setState('polling');
    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const p = await getProgreso(recId);
        if (!mountedRef.current) return;
        setProgreso(p);

        if (p.estado === 1) {
          cleanup();
          setState('completed');
        } else if (p.estado === 2) {
          cleanup();
          setState('error');
          setErrorMsg(p.errorDetalle || 'Error desconocido durante la ejecución');
        }
      } catch {
        // Silently continue polling on transient errors
      }
    };

    // Primer poll inmediato
    poll();
    pollingRef.current = setInterval(poll, 2000);
  }, [cleanup]);

  const launch = useCallback(async () => {
    setState('launching');
    setErrorMsg('');
    setProgreso(null);

    try {
      const result = await lanzarMotor(modo, opcion);
      if (!result.ok) {
        setState('error');
        setErrorMsg(result.error || 'No se pudo lanzar el motor');
        return;
      }
      // Si el backend nos devuelve el Id del ciclo lanzado, sondeamos su progreso directamente.
      // Esto cubre el caso de arranque en frío, en el que el ciclo ya puede estar completado
      // (Estado=1) cuando llega la respuesta y, por tanto, "en curso" ya no lo encontraría.
      if (result.reconciliacionId != null) {
        startPolling(result.reconciliacionId);
        return;
      }
    } catch (err: any) {
      setState('error');
      setErrorMsg(err.message || 'Error al comunicar con el servidor');
      return;
    }

    // Fallback: esperar a que aparezca la fila con Estado=0
    let attempts = 0;
    const maxAttempts = 10;
    const waitForRow = async () => {
      if (!mountedRef.current) return;
      attempts++;
      try {
        const enCurso = await getMotorEnCurso();
        if (enCurso) {
          startPolling(enCurso.id);
          return;
        }
      } catch { /* ignore */ }

      if (attempts >= maxAttempts) {
        setState('launch_failed');
        setErrorMsg('El motor no se ha iniciado correctamente. Revisa los logs del servidor.');
      } else {
        setTimeout(waitForRow, 1000);
      }
    };

    setTimeout(waitForRow, 1000);
  }, [modo, opcion, startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    if (visible) {
      launch();
    }
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [visible, launch, cleanup]);

  const handleClose = () => {
    cleanup();
    onClose();
    if (state === 'completed') {
      onCompleted();
    }
  };

  const handleRetry = () => {
    launch();
  };

  const getProgressPercent = (): number => {
    if (!progreso || progreso.fase !== 5 || progreso.farmaciasAnalizadas === 0) return 0;
    return Math.round((progreso.farmaciasProcesadas / progreso.farmaciasAnalizadas) * 100);
  };

  const renderFaseIcon = (faseUI: FaseUI) => {
    if (!progreso) return <MinusCircleOutlined style={{ color: '#d9d9d9' }} />;

    if (progreso.fase >= faseUI.completedWhenFase) {
      return <CheckCircleFilled style={{ color: '#52c41a' }} />;
    }

    // Determinar si esta fase es la activa
    const prevFase = FASES_UI[FASES_UI.indexOf(faseUI) - 1];
    const isActive = !prevFase || progreso.fase >= prevFase.completedWhenFase;

    if (isActive && progreso.fase < faseUI.completedWhenFase) {
      return <LoadingOutlined style={{ color: '#1677ff' }} />;
    }

    return <MinusCircleOutlined style={{ color: '#d9d9d9' }} />;
  };

  const renderFases = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
        {FASES_UI.map((faseUI) => {
          const detail = progreso && faseUI.getDetail ? faseUI.getDetail(progreso) : null;
          const isProgressPhase = faseUI.hasProgress && progreso?.fase === 5;

          return (
            <div key={faseUI.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ marginTop: 2, fontSize: 16 }}>{renderFaseIcon(faseUI)}</span>
              <div style={{ flex: 1 }}>
                <div>
                  <Text strong={progreso !== null && progreso.fase >= (FASES_UI[FASES_UI.indexOf(faseUI) - 1]?.completedWhenFase ?? 0) && progreso.fase < faseUI.completedWhenFase}>
                    {faseUI.label}
                  </Text>
                  {detail && (
                    <Text type="secondary" style={{ marginLeft: 12 }}>{detail}</Text>
                  )}
                </div>
                {isProgressPhase && progreso!.farmaciasAnalizadas > 0 && (
                  <Progress
                    percent={getProgressPercent()}
                    size="small"
                    style={{ marginTop: 4, maxWidth: 300 }}
                    strokeColor="#1677ff"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    if (state === 'launching') {
      return (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <LoadingOutlined style={{ fontSize: 32, color: '#1677ff' }} />
          <div style={{ marginTop: 12 }}>
            <Text>Lanzando motor de reconciliación...</Text>
          </div>
        </div>
      );
    }

    if (state === 'launch_failed' || state === 'error') {
      return (
        <div style={{ padding: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
            <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 20, marginTop: 2 }} />
            <div>
              <Text strong>Error durante la reconciliación</Text>
              {progreso && state === 'error' && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">Fase alcanzada: {FASE_NAMES[progreso.fase] || `Fase ${progreso.fase}`}</Text>
                </div>
              )}
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff2f0', borderRadius: 4 }}>
                <Text type="danger">{errorMsg}</Text>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleClose}>Cerrar</Button>
              <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry}>Reintentar</Button>
            </Space>
          </div>
        </div>
      );
    }

    if (state === 'completed') {
      return (
        <div style={{ padding: '16px 0' }}>
          {renderFases()}
          {progreso && (
            <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
              <Text strong style={{ color: '#52c41a' }}>Reconciliación completada</Text>
              <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Text type="secondary">Operaciones: {progreso.totalOperaciones}</Text>
                <Text type="secondary">Excepciones: {progreso.totalExcepciones}</Text>
                <Text type="secondary">Invariantes: {progreso.totalInvariantes}</Text>
              </div>
            </div>
          )}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button type="primary" onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      );
    }

    // state === 'polling'
    return (
      <div style={{ padding: '16px 0' }}>
        {renderFases()}
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      closable={state === 'completed' || state === 'error' || state === 'launch_failed'}
      maskClosable={false}
      footer={null}
      onCancel={handleClose}
      title={<Title level={5} style={{ margin: 0 }}>Reconciliación en curso</Title>}
      width={520}
    >
      {renderContent()}
    </Modal>
  );
};

export default MotorProgressOverlay;
